import React, { useCallback, useContext, useEffect, useState } from "react";
import Header from "components/layout/Header";
import { Button, Grid, InputAdornment, SelectChangeEvent, TextField, Typography } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import commonStyles from "styles/common.module.scss";
import styles from "./VerifyCredentials.module.scss";
import CredentialService from "services/api/CredentialService";
import { UserContext } from "components/UserGuard";
import routes from "store/configs/Routes";
import { NavLink, useHistory, useParams } from "react-router-dom";
import Spinner from "components/shared/Spinner";
import clipboards from "assets/icons/clipboards.svg";
import { Completions } from "store/types/Completions";
import searchNormal from "assets/icons/searchNormal.svg";
import printer from "assets/icons/printer.svg";
import { useSnackbar } from "notistack";
import sideCarrot from "assets/icons/sideCarrot.svg";
import CreditEngineService from "services/api/CreditEngineService";
import { useWindowSize } from "util/Window";
import { defaultGridContainerProps, defaultGridItemProps } from "util/Layout";
import { isNullOrUndefined } from "util";
import classNames from "classnames";

const MAX_ROWS = 50;

const VerifyCredentials: React.FunctionComponent = () => {
  const { isMobile } = useWindowSize();
  const { programs } = useContext(UserContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [credsList, setCredsList] = useState<Completions[]>();
  const [searchVal, setSearchVal] = React.useState<string>();
  const { nccerNumbers } = useParams<{ nccerNumbers: string }>();
  const [selectedProgram, setProgram] = React.useState<string>();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  let results: Completions[] | undefined;

  const searchNumbers = useCallback(
    (inheritedSearch?: string) => {
      if ((!searchVal || searchVal == "") && !inheritedSearch) {
        //raise toastr error?
      } else {
        setLoading(true);
        const formattedSearch = searchVal?.replaceAll("\n", ";\n")?.replaceAll("\t", ",");
        //history.push(`${routes.verifyCredentials}/${formattedSearch ?? inheritedSearch}`);
        CredentialService.search(inheritedSearch || formattedSearch!, selectedProgram ?? "")
          .then((response) => {
            results = response.resultsFound;
            setCredsList(results);
            const successList: string[] = [];
            for (let i = 0; i < results.length ?? 0; i++) {
              successList.push(results[i].CardNumber);
            }
            history.push(`${routes.verifyCredentials}/${successList.join(";")}`);
            if (response.lookupErrors != null && response.lookupErrors.length > 0) {
              response.lookupErrors.forEach((element) => {
                enqueueSnackbar(element, { variant: "warning" });
              });
            }
            setLoading(false);
          })
          .catch((error) => {
            if (error.code !== 404) {
              enqueueSnackbar(error.body, { variant: "error" });
            }
            results = undefined;
            setCredsList(results);
            setLoading(false);
          });
      }
    },
    [searchVal, enqueueSnackbar, setLoading, setCredsList]
  );

  useEffect(() => {
    setSearchVal(nccerNumbers?.replaceAll(";", "\n"));
    searchNumbers(nccerNumbers);
  }, [setSearchVal]);

  const printVerificationReport = useCallback(() => {
    if (nccerNumbers) {
      CreditEngineService.getVerificationReport(nccerNumbers)
        .then(() => {
          enqueueSnackbar(`Downloading file`, { variant: "success" });
        })
        .catch((error: string) => {
          enqueueSnackbar(error, { variant: "error" });
        });
    }
  }, [enqueueSnackbar, nccerNumbers]);

  const handleEnterKey = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      searchNumbers();
    }
  };
  const handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    // allow alphanumeric, commas, semicolons, spaces, and new-lines only
    const regex = /^[0-9a-zA-Z,;\-\n \t\b]+$/;
    if (event.currentTarget.value === "" || regex.test(event.currentTarget.value)) {
      const count = (event.currentTarget.value.match(/\n/g) || []).length;
      if (count < MAX_ROWS) {
        setSearchVal(event.currentTarget.value);
      } else {
        setSearchVal(event.currentTarget.value.split("\n", MAX_ROWS).join("\n"));
      }
    }
  };

  const handleProgramSelect = (event: SelectChangeEvent) => {
    setProgram(event.target.value);
  };

  return (
    <>
      {!isMobile && <Header />}
      <div className={styles.verifyCredentialsHeader}>
        <Grid {...defaultGridContainerProps}>
          <Grid {...defaultGridItemProps}>
            <Typography variant={"h2"} className={styles.title}>
              {"Verify Credentials"}
            </Typography>
            <Typography variant={"body1"} className={styles.description}>
              {"Enter NCCER number(s) or Last Name and SSN. Separate entries by line or paste a list."}
            </Typography>
          </Grid>
          {!isMobile ? (
            <Grid {...defaultGridItemProps} md={12} sx={{ display: "flex", flexDirection: "row", gap: "16px" }}>
              <TextField
                placeholder={"123456 or Smith 123-45-6789"}
                value={searchVal}
                size={"small"}
                onChange={handleSearchInput}
                //onKeyDown={handleEnterKey}
                className={styles.searchBar}
                multiline
                maxRows={10}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position={"start"}>
                      <img src={searchNormal} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant={"outlined"}
                color={"secondary"}
                className={styles.searchButton}
                onClick={() => searchNumbers()}
                disabled={loading}
              >
                <img src={searchNormal} className={styles.searchIcon} />
                <span className={styles.text}>{"Search"}</span>
              </Button>
              <Button
                variant={"contained"}
                color={"secondary"}
                className={styles.exportButton}
                onClick={printVerificationReport}
                disabled={loading || !credsList?.length}
              >
                {/* <img src={printer} className={styles.searchIcon} /> */}
                <span className={classNames(styles.exportText)}>{"Export Results"}</span>
              </Button>
            </Grid>
          ) : (
            <>
              <Grid {...defaultGridItemProps}>
                <TextField
                  placeholder={"123456 or Smith 123-45-6789"}
                  value={searchVal}
                  size={"small"}
                  onChange={handleSearchInput}
                  //onKeyDown={handleEnterKey}
                  className={styles.searchBar}
                  multiline
                  maxRows={10}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position={"start"}>
                        <img src={searchNormal} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid {...defaultGridItemProps} sx={{ paddingTop: "8px !important" }}>
                <Button
                  variant={"outlined"}
                  color={"secondary"}
                  fullWidth={true}
                  className={styles.searchButton}
                  onClick={() => searchNumbers()}
                  disabled={loading}
                >
                  <img src={searchNormal} className={styles.searchIcon} />
                  <span className={styles.text}>{"Search"}</span>
                </Button>
              </Grid>
              <Grid {...defaultGridItemProps} sx={{ paddingTop: "8px !important" }}>
                <Button
                  variant={"contained"}
                  color={"secondary"}
                  fullWidth={true}
                  className={styles.exportButton}
                  onClick={printVerificationReport}
                  disabled={loading || !credsList?.length}
                >
                  <span className={classNames(styles.exportText)}>{"Export Results"}</span>
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </div>
      <Spinner loading={loading}>
        <div className={commonStyles.pageContent}>
          {credsList &&
            credsList.map((cred, index) => {
              if (cred.Completions.length > 0 || cred.Credentials.length > 0) {
                return (
                  <Grid container key={index} className={styles.credentialCard}>
                    <Grid item xs={8} className={commonStyles.nameplateWrapper}>
                      <Grid item xs={2} md={1} xl={1} className={commonStyles.smallProfileImageWrapper}>
                        {cred.LogoUrl ? (
                          <img className={commonStyles.smallProfileImage} src={cred.LogoUrl} />
                        ) : (
                          <AccountCircleIcon className={commonStyles.smallProfileImage}></AccountCircleIcon>
                        )}
                      </Grid>
                      <Grid item xs={10} md={11} xl={11} className={commonStyles.nameplate}>
                        <Typography variant={"h2"}>
                          {cred.FirstName} {cred.LastName}
                        </Typography>
                        <Typography variant={"body2"}>{cred.CardNumber}</Typography>
                      </Grid>
                    </Grid>
                    <Grid item xs={4}>
                      <NavLink to={routes.viewCredentials + "/" + cred.CardNumber}>
                        <Button size={"small"} className={styles.viewCredsBtn}>
                          <Typography variant={"body1"} className={styles.text}>
                            {"View Credentials"}
                            <img src={sideCarrot} className={styles.carrot} />
                          </Typography>
                        </Button>
                      </NavLink>
                    </Grid>
                  </Grid>
                );
              }
            })}
          {(isNullOrUndefined(credsList) || credsList?.length == 0) && !loading && (
            <Grid item xs={12} className={commonStyles.clipboardBox}>
              <img src={clipboards} />
              <Typography variant={"h5"} className={commonStyles.text}>
                {
                  "Sorry, we can't find any records that match your search. Please try re-entering the information and make sure that it is accurate."
                }
              </Typography>
            </Grid>
          )}
        </div>
      </Spinner>
    </>
  );
};
export default VerifyCredentials;
